import { useCallback, useMemo } from 'react';
import { useGetUserStorageQuery } from '@ports/internal-services/userDetails/userDetailsSlice';

type AvailableStorageQueryData = {
  subscriberId?: string;
  subscriptionPlanName?: string;
  usedSize?: number;
  subscriptionSize?: number;
};

type ValidateSpaceResult = {
  ok: boolean;
  requiredSizeGb: number;
  availableStorageGb: number;
  totalStorageGb: number;
  usedStorageGb: number;
  error?: unknown;
};

const formatNumber = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) {
    return '0.00';
  }

  const rounded2 = Number(value.toFixed(2));
  if (Math.abs(rounded2 - value) < Number.EPSILON) {
    return rounded2.toFixed(2);
  }

  const rounded3 = Number(value.toFixed(3));
  if (Math.abs(rounded3 - value) < Number.EPSILON) {
    return rounded3.toFixed(3);
  }

  return value.toFixed(3).replace(/\.?(0+)$/, '');
};

const formatStorageLabel = (value?: number) => {
  if (typeof value !== 'number' || isNaN(value) || value <= 0) {
    return '0.00 GB';
  }

  if (value < 1) {
    const valueMb = value * 1024;
    return `${formatNumber(valueMb)} MB`;
  }

  return `${formatNumber(value)} GB`;
};

const bytesToGb = (bytes: number) => bytes / (1024 * 1024 * 1024);

export const useAvailableStorage = () => {
  const { data, error, isLoading, isFetching, isSuccess, refetch } =
    useGetUserStorageQuery();

  const responseData = data?.data ?? data;
  const storageData = responseData as AvailableStorageQueryData | undefined;

  const usedStorageGb = useMemo(
    () => bytesToGb(storageData?.usedSize ?? 0),
    [storageData?.usedSize]
  );
  const totalStorageGb = storageData?.subscriptionSize ?? 0;

  const availableStorage = useMemo(
    () => Math.max(totalStorageGb - usedStorageGb, 0),
    [totalStorageGb, usedStorageGb]
  );

  const validateSpace = useCallback(
    async (files?: File[]): Promise<ValidateSpaceResult> => {
      const requiredSizeGb = files
        ? files.reduce((sum, file) => sum + bytesToGb(file.size), 0)
        : 0;

      try {
        const result = await refetch();
        if ('error' in result && result.error) {
          return {
            ok: false,
            requiredSizeGb,
            availableStorageGb: availableStorage,
            totalStorageGb: totalStorageGb,
            usedStorageGb: usedStorageGb,
            error: result.error
          };
        }

        const latestData = (result.data?.data ?? result.data) as
          | AvailableStorageQueryData
          | undefined;

        const latestUsedGb = bytesToGb(latestData?.usedSize ?? 0);
        const latestTotalGb = latestData?.subscriptionSize ?? 0;
        const latestAvailableGb = Math.max(latestTotalGb - latestUsedGb, 0);

        return {
          ok: latestAvailableGb >= requiredSizeGb,
          requiredSizeGb,
          availableStorageGb: latestAvailableGb,
          totalStorageGb: latestTotalGb,
          usedStorageGb: latestUsedGb
        };
      } catch (err) {
        return {
          ok: false,
          requiredSizeGb,
          availableStorageGb: availableStorage,
          totalStorageGb: totalStorageGb,
          usedStorageGb: usedStorageGb,
          error: err
        };
      }
    },
    [availableStorage, refetch, totalStorageGb, usedStorageGb]
  );

  return {
    availableStorage,
    availableStorageLabel: formatStorageLabel(availableStorage),
    usedStorage: usedStorageGb,
    usedStorageLabel: formatStorageLabel(usedStorageGb),
    totalStorage: totalStorageGb,
    totalStorageLabel: formatStorageLabel(totalStorageGb),
    subscriptionPlanName: storageData?.subscriptionPlanName,
    subscriberId: storageData?.subscriberId,
    storageData,
    isLoading,
    isFetching,
    isSuccess,
    isError: !!error,
    error,
    refetchStorage: refetch,
    validateSpace
  };
};
